import React, { useCallback, useState } from 'react'
import { useDropzone, FileRejection } from 'react-dropzone'

export interface UploadedFile {
  file: File
  label: string
  type: 'audio' | 'video' | 'document'
}

interface FileUploadZoneProps {
  onFilesAdded: (files: UploadedFile[]) => void
  acceptedFileTypes?: 'media' | 'documents' | 'all'
  maxFiles?: number
  disabled?: boolean
}

const MEDIA_EXTENSIONS = {
  'audio/*': ['.mp3', '.wav', '.m4a', '.ogg', '.flac', '.aac'],
  'video/*': ['.mp4', '.mkv', '.avi', '.mov', '.webm'],
}

const DOCUMENT_EXTENSIONS = {
  'application/pdf': ['.pdf'],
  'application/vnd.openxmlformats-officedocument.presentationml.presentation': ['.pptx'],
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
}

const getFileType = (file: File): 'audio' | 'video' | 'document' => {
  const ext = '.' + file.name.split('.').pop()?.toLowerCase()

  if (DOCUMENT_EXTENSIONS['application/pdf'].includes(ext) ||
      DOCUMENT_EXTENSIONS['application/vnd.openxmlformats-officedocument.presentationml.presentation'].includes(ext) ||
      DOCUMENT_EXTENSIONS['application/vnd.openxmlformats-officedocument.wordprocessingml.document'].includes(ext)) {
    return 'document'
  }

  if (MEDIA_EXTENSIONS['video/*'].includes(ext)) {
    return 'video'
  }

  return 'audio'
}

const FileUploadZone: React.FC<FileUploadZoneProps> = ({
  onFilesAdded,
  acceptedFileTypes = 'all',
  maxFiles = 10,
  disabled = false,
}) => {
  const [uploadError, setUploadError] = useState<string | null>(null)

  const getAcceptedTypes = () => {
    if (acceptedFileTypes === 'media') {
      return MEDIA_EXTENSIONS
    }
    if (acceptedFileTypes === 'documents') {
      return DOCUMENT_EXTENSIONS
    }
    return { ...MEDIA_EXTENSIONS, ...DOCUMENT_EXTENSIONS }
  }

  const onDrop = useCallback(
    (acceptedFiles: File[], rejectedFiles: FileRejection[]) => {
      setUploadError(null)

      if (rejectedFiles.length > 0) {
        const errors = rejectedFiles.map(
          (r) => `${r.file.name}: ${r.errors.map((e) => e.message).join(', ')}`
        )
        setUploadError(errors.join('\n'))
        return
      }

      const uploadedFiles: UploadedFile[] = acceptedFiles.map((file) => ({
        file,
        label: file.name.replace(/\.[^/.]+$/, ''), // Remove extension for default label
        type: getFileType(file),
      }))

      onFilesAdded(uploadedFiles)
    },
    [onFilesAdded]
  )

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: getAcceptedTypes(),
    maxFiles,
    disabled,
  })

  return (
    <div>
      <div
        {...getRootProps()}
        className={`
          border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-colors
          ${isDragActive
            ? 'border-orange-400 bg-orange-50'
            : 'border-gray-300 hover:border-orange-400 hover:bg-orange-50/50'
          }
          ${disabled ? 'opacity-50 cursor-not-allowed' : ''}
        `}
      >
        <input {...getInputProps()} />

        <div className="flex flex-col items-center gap-3">
          <div className={`p-3 rounded-full ${isDragActive ? 'bg-orange-100' : 'bg-gray-100'}`}>
            <svg
              className={`w-8 h-8 ${isDragActive ? 'text-orange-500' : 'text-gray-400'}`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
              />
            </svg>
          </div>

          {isDragActive ? (
            <p className="text-orange-600 font-medium">Отпустите файлы здесь...</p>
          ) : (
            <>
              <p className="text-gray-600">
                <span className="font-medium text-orange-600">Нажмите для выбора</span>
                {' '}или перетащите файлы сюда
              </p>
              <p className="text-sm text-gray-400">
                {acceptedFileTypes === 'media' && 'MP3, WAV, M4A, MP4, MKV, AVI'}
                {acceptedFileTypes === 'documents' && 'PDF, PPTX, DOCX'}
                {acceptedFileTypes === 'all' && 'Аудио, видео или документы (PDF, PPTX)'}
              </p>
            </>
          )}
        </div>
      </div>

      {uploadError && (
        <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-600">
          {uploadError}
        </div>
      )}
    </div>
  )
}

export default FileUploadZone
